import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { Field, FormDefinition, FormDocument, FormVersion, Section } from '../types';
import type { EmbeddedToHostMessage, HostToEmbeddedMessage } from '../types/messages';
import { createFieldTypeRegistry, type FieldTypeDefinition, type FieldTypeRegistry } from '../fieldTypes/registry';
import { createField, createSection } from '../utils/createField';

// ─── Callbacks (direct-React mode) ─────────────────────────────────────────

export interface FormBuilderCallbacks {
  onSaveDraft: (version: FormVersion) => void;
  onPublish: (version: FormVersion) => void;
  onCreateVersion: (fromVersionId: string) => void;
  onRestoreVersion: (versionId: string) => void;
  onArchiveVersion: (versionId: string) => void;
  onFieldSelected?: (field: Field | null) => void;
  onDirtyChange?: (isDirty: boolean) => void;
  /** When supplied, the Toolbar renders a "← Back" button that calls this instead of the
   *  host having to render its own separate navigation bar above the component. */
  onNavigateBack?: () => void;
  /** When supplied, a Form Details bar (Name/Description) renders below the Toolbar,
   *  editing FormDefinition metadata — independent of draft/published version status,
   *  since renaming a form isn't a content edit. */
  onUpdateDefinition?: (patch: Partial<FormDefinition>) => void;
  /**
   * When supplied, an "Import from Screenshot" button appears in the Toolbar.
   * The host is responsible for opening a file picker, calling the vision API,
   * and returning a parsed FormDocument — or null to cancel.
   */
  onImportFromScreenshot?: () => Promise<FormDocument | null>;
}

// ─── Context shape ──────────────────────────────────────────────────────────

interface FormBuilderContextValue {
  formDefinition: FormDefinition;
  versions: FormVersion[];
  activeVersion: FormVersion;
  document: FormDocument;
  fieldTypeRegistry: FieldTypeRegistry;
  isDirty: boolean;
  isEditable: boolean;
  readOnly: boolean;
  /** True when the form was just created — enables Form Type selection, which is locked after initial save. */
  isNewForm: boolean;
  selectedFieldId: string | null;
  selectField: (fieldId: string | null) => void;
  selectedField: Field | null;
  previewOpen: boolean;
  togglePreview: () => void;

  addSection: (parentId?: string) => void;
  updateSection: (sectionId: string, patch: Partial<Section>) => void;
  removeSection: (sectionId: string) => void;
  /** Reorders siblings — pass only the ids of sections sharing the same parentId. */
  reorderSections: (orderedIds: string[]) => void;

  addField: (sectionId: string, fieldType: string, atIndex?: number) => void;
  updateField: (fieldId: string, patch: Partial<Field>) => void;
  removeField: (fieldId: string) => void;
  moveField: (fieldId: string, toSectionId: string, toIndex: number) => void;

  saveDraft: () => void;
  publish: () => void;
  createVersionFromCurrent: () => void;
  restoreVersion: (versionId: string) => void;
  archiveVersion: (versionId: string) => void;

  /** Defined when the host supplied onImportFromScreenshot. Handles loading state internally. */
  importFromScreenshot?: () => Promise<void>;
  isImporting: boolean;
  importError: string | null;
  clearImportError: () => void;

  onNavigateBack?: () => void;
  onUpdateDefinition?: (patch: Partial<FormDefinition>) => void;
}

const FormBuilderContext = createContext<FormBuilderContextValue | null>(null);

export function useFormBuilder(): FormBuilderContextValue {
  const ctx = useContext(FormBuilderContext);
  if (!ctx) throw new Error('useFormBuilder must be used inside FormBuilderProvider');
  return ctx;
}

// ─── Provider ───────────────────────────────────────────────────────────────

interface ProviderProps {
  formDefinition: FormDefinition;
  versions: FormVersion[];
  activeVersion: FormVersion;
  fieldTypes?: FieldTypeDefinition[];
  callbacks: FormBuilderCallbacks;
  iframeMode?: boolean;
  readOnly?: boolean;
  /** Pass true immediately after creating a new form to enable Form Type selection. */
  isNewForm?: boolean;
  children: React.ReactNode;
}

function findField(document: FormDocument, fieldId: string): Field | null {
  for (const section of document.sections) {
    const field = section.fields.find((f) => f.id === fieldId);
    if (field) return field;
  }
  return null;
}

export function FormBuilderProvider({
  formDefinition,
  versions,
  activeVersion: activeVersionProp,
  fieldTypes,
  callbacks,
  iframeMode = false,
  readOnly = false,
  isNewForm = false,
  children,
}: ProviderProps) {
  const fieldTypeRegistry = useMemo(() => createFieldTypeRegistry(fieldTypes), [fieldTypes]);

  const [activeVersion, setActiveVersion] = useState<FormVersion>(activeVersionProp);
  const [isDirty, setIsDirty] = useState(false);
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const clearImportError = useCallback(() => setImportError(null), []);

  // Sync from host whenever the host pushes a different version (e.g. after Save/Publish round-trips).
  useEffect(() => {
    setActiveVersion(activeVersionProp);
    setIsDirty(false);
  }, [activeVersionProp]);

  const emit = useCallback(
    (msg: EmbeddedToHostMessage) => {
      if (iframeMode) window.parent.postMessage(msg, '*');
    },
    [iframeMode],
  );

  useEffect(() => {
    if (!iframeMode) return;
    const handler = (e: MessageEvent) => {
      const msg = e.data as HostToEmbeddedMessage;
      if (!msg?.type) return;
      switch (msg.type) {
        case 'SET_ACTIVE_VERSION':
          setActiveVersion(msg.payload);
          setIsDirty(false);
          break;
        case 'SET_VERSIONS':
        case 'SET_FORM_DEFINITION':
        case 'VALIDATION_RESULT':
          // Definition/version lists and validation results are read directly from props
          // in this host-driven design; iframe hosts re-render with new props on their side.
          break;
      }
    };
    window.addEventListener('message', handler);
    emit({ type: 'READY' });
    return () => window.removeEventListener('message', handler);
  }, [iframeMode, emit]);

  const wasDirtyRef = useRef(false);
  const markDirty = useCallback(() => {
    setIsDirty(true);
    if (!wasDirtyRef.current) {
      wasDirtyRef.current = true;
      callbacks.onDirtyChange?.(true);
      emit({ type: 'DIRTY_CHANGED', payload: { isDirty: true } });
    }
  }, [callbacks, emit]);

  const isEditable = !readOnly && activeVersion.status === 'draft';

  const updateDocument = useCallback(
    (updater: (doc: FormDocument) => FormDocument) => {
      if (!isEditable) return;
      setActiveVersion((prev) => ({ ...prev, document: updater(prev.document) }));
      markDirty();
    },
    [isEditable, markDirty],
  );

  // ── Section actions ──────────────────────────────────────────────────────

  const addSection = useCallback(
    (parentId?: string) => {
      updateDocument((doc) => {
        const siblingCount = doc.sections.filter((s) => s.parentId === parentId).length;
        return { sections: [...doc.sections, createSection(siblingCount, parentId)] };
      });
    },
    [updateDocument],
  );

  const updateSection = useCallback(
    (sectionId: string, patch: Partial<Section>) => {
      updateDocument((doc) => ({
        sections: doc.sections.map((s) => (s.id === sectionId ? { ...s, ...patch } : s)),
      }));
    },
    [updateDocument],
  );

  const removeSection = useCallback(
    (sectionId: string) => {
      updateDocument((doc) => {
        // Cascade: removing a section also removes its nested subsections (and theirs, ...).
        const toRemove = new Set<string>([sectionId]);
        let grew = true;
        while (grew) {
          grew = false;
          for (const s of doc.sections) {
            if (s.parentId && toRemove.has(s.parentId) && !toRemove.has(s.id)) {
              toRemove.add(s.id);
              grew = true;
            }
          }
        }
        return { sections: doc.sections.filter((s) => !toRemove.has(s.id)) };
      });
      setSelectedFieldId(null);
    },
    [updateDocument],
  );

  const reorderSections = useCallback(
    (orderedIds: string[]) => {
      updateDocument((doc) => {
        // Only touches the order of the given ids (siblings) — sections outside this
        // group (other branches, other nesting levels) must be left untouched.
        const orderById = new Map(orderedIds.map((id, i) => [id, i]));
        return {
          sections: doc.sections.map((s) => (orderById.has(s.id) ? { ...s, order: orderById.get(s.id)! } : s)),
        };
      });
    },
    [updateDocument],
  );

  // ── Field actions ────────────────────────────────────────────────────────

  const addField = useCallback(
    (sectionId: string, fieldType: string, atIndex?: number) => {
      updateDocument((doc) => ({
        sections: doc.sections.map((s) => {
          if (s.id !== sectionId) return s;
          const newField = createField(fieldType, s, fieldTypeRegistry);
          const fields = [...s.fields];
          const insertAt = atIndex ?? fields.length;
          fields.splice(insertAt, 0, newField);
          return { ...s, fields: fields.map((f, i) => ({ ...f, order: i })) };
        }),
      }));
    },
    [updateDocument, fieldTypeRegistry],
  );

  const updateField = useCallback(
    (fieldId: string, patch: Partial<Field>) => {
      updateDocument((doc) => ({
        sections: doc.sections.map((s) => ({
          ...s,
          fields: s.fields.map((f) => (f.id === fieldId ? { ...f, ...patch } : f)),
        })),
      }));
    },
    [updateDocument],
  );

  const removeField = useCallback(
    (fieldId: string) => {
      updateDocument((doc) => ({
        sections: doc.sections.map((s) => ({
          ...s,
          fields: s.fields.filter((f) => f.id !== fieldId).map((f, i) => ({ ...f, order: i })),
        })),
      }));
      setSelectedFieldId((cur) => (cur === fieldId ? null : cur));
    },
    [updateDocument],
  );

  const moveField = useCallback(
    (fieldId: string, toSectionId: string, toIndex: number) => {
      updateDocument((doc) => {
        let moved: Field | undefined;
        const stripped = doc.sections.map((s) => {
          const found = s.fields.find((f) => f.id === fieldId);
          if (found) moved = found;
          return { ...s, fields: s.fields.filter((f) => f.id !== fieldId) };
        });
        if (!moved) return doc;
        return {
          sections: stripped.map((s) => {
            if (s.id !== toSectionId) return s;
            const fields = [...s.fields];
            fields.splice(toIndex, 0, { ...moved!, sectionId: toSectionId });
            return { ...s, fields: fields.map((f, i) => ({ ...f, order: i })) };
          }),
        };
      });
    },
    [updateDocument],
  );

  // ── Selection ────────────────────────────────────────────────────────────

  const selectField = useCallback(
    (fieldId: string | null) => {
      setSelectedFieldId(fieldId);
      const field = fieldId ? findField(activeVersion.document, fieldId) : null;
      callbacks.onFieldSelected?.(field);
      emit({ type: 'FIELD_SELECTED', payload: { fieldId } });
    },
    [activeVersion.document, callbacks, emit],
  );

  const selectedField = selectedFieldId ? findField(activeVersion.document, selectedFieldId) : null;

  // ── Versioning actions ───────────────────────────────────────────────────

  const saveDraft = useCallback(() => {
    callbacks.onSaveDraft(activeVersion);
    emit({ type: 'SAVE_DRAFT', payload: activeVersion });
    emit({ type: 'DRAFT_SAVED', payload: { formVersionId: activeVersion.formVersionId } });
    setIsDirty(false);
    wasDirtyRef.current = false;
    callbacks.onDirtyChange?.(false);
    emit({ type: 'DIRTY_CHANGED', payload: { isDirty: false } });
  }, [activeVersion, callbacks, emit]);

  const publish = useCallback(() => {
    callbacks.onPublish(activeVersion);
    emit({ type: 'PUBLISH_VERSION', payload: { formVersionId: activeVersion.formVersionId } });
    emit({ type: 'VERSION_PUBLISHED', payload: { formVersionId: activeVersion.formVersionId } });
  }, [activeVersion, callbacks, emit]);

  const createVersionFromCurrent = useCallback(() => {
    callbacks.onCreateVersion(activeVersion.formVersionId);
    emit({ type: 'CREATE_VERSION', payload: { fromVersionId: activeVersion.formVersionId } });
  }, [activeVersion.formVersionId, callbacks, emit]);

  const restoreVersion = useCallback(
    (versionId: string) => {
      callbacks.onRestoreVersion(versionId);
      emit({ type: 'RESTORE_VERSION', payload: { formVersionId: versionId } });
    },
    [callbacks, emit],
  );

  const archiveVersion = useCallback(
    (versionId: string) => {
      callbacks.onArchiveVersion(versionId);
      emit({ type: 'ARCHIVE_VERSION', payload: { formVersionId: versionId } });
    },
    [callbacks, emit],
  );

  const togglePreview = useCallback(() => setPreviewOpen((v) => !v), []);

  const importFromScreenshot = callbacks.onImportFromScreenshot
    ? async () => {
        setIsImporting(true);
        try {
          const imported = await callbacks.onImportFromScreenshot!();
          if (imported) {
            updateDocument(() => imported);
            setSelectedFieldId(null);
          }
        } catch {
          setImportError('Screenshot import failed. Please try again or contact support if the problem persists.');
        } finally {
          setIsImporting(false);
        }
      }
    : undefined;

  const value: FormBuilderContextValue = {
    formDefinition,
    versions,
    activeVersion,
    document: activeVersion.document,
    fieldTypeRegistry,
    isDirty,
    isEditable,
    readOnly,
    isNewForm,
    selectedFieldId,
    selectField,
    selectedField,
    previewOpen,
    togglePreview,
    addSection,
    updateSection,
    removeSection,
    reorderSections,
    addField,
    updateField,
    removeField,
    moveField,
    saveDraft,
    publish,
    createVersionFromCurrent,
    restoreVersion,
    archiveVersion,
    importFromScreenshot,
    isImporting,
    importError,
    clearImportError,
    onNavigateBack: callbacks.onNavigateBack,
    onUpdateDefinition: callbacks.onUpdateDefinition,
  };

  return <FormBuilderContext.Provider value={value}>{children}</FormBuilderContext.Provider>;
}
